import {find, map, takeRight} from 'lodash';
import moment from 'moment';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SocketLikeWithNamespace} from 'socket.io-react-hook/lib/cjs/types';

import {PartialSessionChangeName, SocketEvent} from 'enums';
import {useSocketEventCallback} from 'hooks';
import {AnyParticipant, Session, SessionActivity, SessionActivityName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {DefaultParticipant, PartialSessionChange} from 'types';
import {sessionUtils} from 'utils';

import {SubtitlesMessages} from './SubtitlesMessages.tsx/SubtitlesMessages';

type Props = {
    session: Session<any>;
    activity: SessionActivity;
    socket: SocketLikeWithNamespace;
}

type SocketMessage = {
    id: string;
    participantId: string;
    participantName: string;
    text: string;
    time: Date;
    timeout: NodeJS.Timeout;
}

const MESSAGE_LIVE_TIME = 5000;
const MAX_MESSAGES = 5;

export const Subtitles: React.FC<Props> = ({socket, session, activity}) => {
    const {t} = useTranslation();
    const [messages, setMessages] = useState<SocketMessage[]>([]);

    const activityData = useMemo(() => activity?.data, [activity]);

    const localParticipantId = useMemo(() => session.localParticipant.id, [session]);

    const getTranslatedMessage = useCallback((change: PartialSessionChange, participantId: string) => {
        const localLanguage = activityData?.translationSettings?.[localParticipantId]?.language;
        if (participantId !== localParticipantId && localLanguage) {
            return change?.data?.translation?.[localLanguage]?.[participantId]?.[change.data.message.id]?.content ||
                change.data.message.content;
        }
        return change.data.message.content;
    }, [activityData, localParticipantId]);

    const handleMessageReceived = useCallback((change: PartialSessionChange, participant: AnyParticipant) => {
        let participantNameInfo = sessionUtils.getParticipantNames(participant);
        if (!participantNameInfo?.name) {
            participantNameInfo = {name: participant.data?.name || participant.id} as any;
        }

        if (!change?.data?.message?.content) {
            return;
        }

        const messageReceived = change.data.message;

        const existingMessage = find(messages, (m) => m.id === messageReceived.id);
        if (existingMessage) {
            setMessages((prev) => map(prev, (message) => {
                if (message.id === messageReceived.id) {
                    return {
                        ...message,
                        text: getTranslatedMessage(change, message.participantId)
                    };
                }
                return message;
            }));
        } else {
            setMessages((prev: SocketMessage[]) => {
                const timeout = setTimeout(() => {
                    setMessages((prev1) => prev1.filter((m) => m.id !== messageReceived.id));
                }, MESSAGE_LIVE_TIME);

                const newMessage: SocketMessage = {
                    id: messageReceived.id,
                    participantId: participant.id,
                    participantName: participant.id === localParticipantId ?
                        t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.YOU) : participantNameInfo.name,
                    text: getTranslatedMessage(change, participant.id),
                    time: moment(messageReceived.date).toDate(),
                    timeout
                };

                return takeRight([...prev, newMessage], MAX_MESSAGES);
            });
        }
    }, [messages, localParticipantId, getTranslatedMessage, t]);

    const handleSocketEvent = useCallback((change: PartialSessionChange) => {
        if (!activity) {
            return;
        }

        if (activity.name === SessionActivityName.Audio && change.name !== PartialSessionChangeName.Audio) {
            return;
        }

        if (activity.name === SessionActivityName.AudioVideo && change.name !== PartialSessionChangeName.AudioVideo) {
            return;
        }

        if (activity.name === SessionActivityName.Avatar && change.name !== PartialSessionChangeName.Avatar) {
            return;
        }

        let participant = sessionUtils.getParticipantById(session, change.data.threadId);
        if (!participant && [DefaultParticipant.Chatbot, DefaultParticipant.System].includes(change.data.threadId as DefaultParticipant)) {
            participant = {
                id: change.data.threadId,
                data: {
                    name: t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.ASSISTANT)
                }
            } as AnyParticipant;
        }
        if (!participant) {
            return;
        }

        handleMessageReceived(change, participant);
    }, [activity, handleMessageReceived, session, t]);

    useSocketEventCallback(socket, SocketEvent.ActiveSessionPartiallyChanged, useCallback((change: PartialSessionChange) => {
        handleSocketEvent(change);
    }, [handleSocketEvent]));

    useEffect(() => () => {
        setMessages([]);
    }, []);

    return (
        <SubtitlesMessages messages={messages} />
    );
};